import React, {useState} from "react";
import {PartnerCard} from "./PartnerCard";
import {IPartner, ITagMetrics, PartnerSideEnum} from "@leap4/entities-discrepancy-common";
import {useQuery} from "react-query";
import {AlertTypes, apiClient, setAlert, Grid, MenuItem, Typography, Skeleton, LoadingButton} from "@leap4/frontend";
import {HomeUrl} from "../../../../../config/generals";
import {metricsComparisonKeyMap} from "../../../../shared/MetricsComparison/MetricsComparisonHelpers";
import {IHomePageOverviewPartnerDates} from "../../interfaces";
import {urlService} from "../../../../routing/urlsService";

export interface IPartnerCardsProps {
    isLoading: boolean,
    startDate: string,
    endDate: string,
    specificComparisonDate: Date,
    newSelectedComparison
}

const cards = (partners, isLoading) =>{
    if (partners?.length > 0){
        return <>
            <Grid container justifyContent={"center"} width={"100%"} margin={"0 auto"} spacing={4}>
                {
                    !isLoading ? partners?.map(singlePartner => {
                        return (
                                <PartnerCard key={singlePartner.partner?._id} partner={singlePartner.partner} totals={singlePartner.totals} compareData={singlePartner.compareData} />
                        )
                    }) : [1, 2].map(s => <Skeleton  variant={"rounded"} sx={{borderRadius: "16px", margin: '10px'}} key={s}
                                                   height={"300px"} width={"450px"} animation={"wave"}/>)
                }
            </Grid>
        </>
    }
    return <></>
}

export const PartnerCards = ({isLoading, startDate, endDate, specificComparisonDate, newSelectedComparison}: IPartnerCardsProps): JSX.Element => {
    const [selectedDemandPartners, setSelectedDemandPartners] = useState< [{partner: IPartner & { _id: string; }; totals: ITagMetrics; compareData: ITagMetrics; }[]]>([[]]);
    const [selectedSupplyPartners, setSelectedSupplyPartners] = useState< [{partner: IPartner & { _id: string; }; totals: ITagMetrics; compareData: ITagMetrics; }[]]>([[]]);
    const [isMoreDemandPartners, setIsMoreDemandPartners] = useState<boolean>(true)
    const [isMoreSupplyPartners, setIsMoreSupplyPartners] = useState<boolean>(true)
    const [demandPage, setDemandPage] = useState<number>(1)
    const [supplyPage, setSupplyPage] = useState<number>(1)

    const onGetDemandPartners = async () => {
        if(!isMoreDemandPartners) {
            return;
        }
        setDemandPage(demandPage + 1)
        const response: IHomePageOverviewPartnerDates = (await apiClient.post(`${HomeUrl}/partners/demand/${demandPage}`, {
            startDate,
            endDate,
            specificComparisonDate: specificComparisonDate || null
        })).data
        const res = response?.overviewTotals?.selectedDates?.partners?.map((pr) => {
            const compareData = response.overviewTotals[metricsComparisonKeyMap[newSelectedComparison]]?.partners?.find(partner => partner.partner._id === pr.partner._id)?.totals
            const {totals, partner} = pr
            return {
                totals,
                partner,
                compareData
            }
        })
        const demandPartners = res?.filter(p => p.partner.side === PartnerSideEnum.DEMAND);
        if (!demandPartners.length || demandPartners.length < 6) {
            setIsMoreDemandPartners(false)
            return;
        }
        selectedDemandPartners.push(demandPartners)
        setSelectedDemandPartners(selectedDemandPartners)
    }

    const onGetSupplyPartners = async () => {
        if(!isMoreSupplyPartners) {
            return;
        }
        setSupplyPage(supplyPage + 1)
        const response: IHomePageOverviewPartnerDates = (await apiClient.post(`${HomeUrl}/partners/supply/${supplyPage}`, {
            startDate,
            endDate,
            specificComparisonDate: specificComparisonDate || null
        })).data
        const res = response?.overviewTotals?.selectedDates?.partners?.map((pr) => {
            const compareData = response.overviewTotals[metricsComparisonKeyMap[newSelectedComparison]]?.partners?.find(partner => partner.partner._id === pr.partner._id)?.totals
            const {totals, partner} = pr
            return {
                totals,
                partner,
                compareData
            }
        })
        const supplyPartners = res?.filter(p => p.partner.side === PartnerSideEnum.SUPPLY);
        if (!supplyPartners.length || supplyPartners.length < 6) {
            setIsMoreSupplyPartners(false)
            return;
        }
        selectedSupplyPartners.push(supplyPartners)
        setSelectedSupplyPartners(selectedSupplyPartners)
    }

    const {isLoading: isLoadingGetDPartner, isRefetching: isRefetchingDPartner, refetch: getDemandPartners} = useQuery(
        'get-demandPartner--query',
        onGetDemandPartners,
        {
            refetchOnWindowFocus: false,
            cacheTime: 0,
            enabled: true,
            onError: (err: any) => setAlert({msg: err.message, type: AlertTypes.FAILURE})
        });

    const {isLoading: isLoadingGetSPartner, isRefetching: isRefetchingSPartner, refetch: getSupplyPartners} = useQuery(
        'get-supplyPartner--query',
        onGetSupplyPartners,
        {
            refetchOnWindowFocus: false,
            cacheTime: 0,
            enabled: true,
            onError: (err: any) => setAlert({msg: err.message, type: AlertTypes.FAILURE})
        });

    return (
        <>
            <Grid item>
                <Grid container justifyContent={"center"} direction={"column"} width={"50%"} margin={"0 auto"}>
                    <Grid item>
                        <Typography variant={"h5"} pt={5}>{"Demand"} Partners</Typography>
                    </Grid>
                </Grid>
                {selectedDemandPartners.map((dp,idx) => <React.Fragment key={idx}>
                {cards(dp, isLoading)}

                </React.Fragment>
                )}
            </Grid>
            <Grid container sx={{ width: {xs: "100%", sm: "50%"}, justifyContent: "center", gap: 2, margin: "0 auto", flexDirection: {xs: "column", sm: "row"}, alignItems: "center" }}>
                <Grid item>
                    <LoadingButton variant="outlined"
                        disabled={!isMoreDemandPartners}
                        loading={isLoadingGetDPartner || isRefetchingDPartner}
                        onClick={() => getDemandPartners()} >
                        {isMoreDemandPartners ? 'Load more' : 'No more partners'}
                    </LoadingButton>
                </Grid>
                <Grid item>
                    <LoadingButton
                        variant="outlined"
                        href={urlService.partnersOverviewDemand()}>
                        All demand partners
                    </LoadingButton>
                </Grid>
            </Grid>
            <Grid item>
                <Grid container justifyContent={"center"} direction={"column"} width={"50%"} margin={"0 auto"}>
                    <Grid item>
                        <Typography variant={"h5"} pt={5}>{"Supply"} Partners</Typography>
                    </Grid>
                </Grid>
                {selectedSupplyPartners.map(dp => cards(dp, isLoading))}
            </Grid>
            <Grid container sx={{ width: {xs: "100%", sm: "50%"}, justifyContent: "center", gap: 2, margin: "0 auto", flexDirection: {xs: "column", sm: "row"}, alignItems: "center" }}>
                <Grid item>
                    <LoadingButton
                        variant="outlined"
                        loading={isLoadingGetSPartner || isRefetchingSPartner}
                        disabled={!isMoreSupplyPartners}
                        onClick={() => getSupplyPartners()} >
                        {isMoreSupplyPartners ? 'Load more' : 'No more partners'}
                    </LoadingButton>
                </Grid>
                <Grid item>
                    <LoadingButton
                        variant="outlined"
                        href={urlService.partnersOverviewSupply()} >
                        All supply partners
                    </LoadingButton>
                </Grid>
            </Grid>
        </>
    )


}